/*

Skio Plan Picker - Usage Notes

3 ways to use the Skio Plan Picker to get the proper "selling_plan" value for making API calls (without form submission)

1. Directly query the <skio-plan-picker></skio-plan-picker> element's selectedSellingPlan property;
   returns the selling plan object (or null if none), of which the id is needed for the API call
   (ex. document.querySelector('skio-plan-picker').selectedSellingPlan)

2. Bind the "selling_plan" input element to update an external input element on change of selectedSellingPlan;
   this works for native form submissions, where an input can be used to store the selling_plan value for the API call

3. Call the addToCart function, which takes in a quantity and runs a /cart/add API call for the selected variant
   with the selected selling plan, and dispatches a 'skio::added-to-cart' event which contains the plan picker's
   key as well as the /cart/add API call's response

If submitting a form to add to cart, pass the formId to the <skio-plan-picker> element to auto-add the necessary form inputs to the form.

For additional questions about the selling_plan object: https://shopify.dev/api/liquid/objects#selling-plan

*/

import { LitElement, html, css } from 'https://cdn.skypack.dev/lit@2.2.8';

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    padding: 0;
    border: 0;
    margin-bottom: 15px;
  }

  .skio-onetime-second {
    order: 2;
  }

  .skio-group-container {
    display: none;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
    border-width: 1px;
    border-color: transparent;
    border-style: solid;
    border-bottom: 1px solid rgb(229, 231, 235);

    padding: 10px 0px;
  }

  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }

  .skio-group-label {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 10px 0;
    overflow: hidden;

  }

  .skio-group-topline {
    display: flex;
    align-items: center;
    width: 100%;
    font-size: 13px;
  }

  .skio-radio-svg {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid currentColor;
    border-radius: 50%;
  }

  .skio-radio-svg.skio-radio-svg--unchecked {
    border: 1px solid;
    opacity: 0.1;
    width: 30px;
    height: 30px;
  }

  .skio-radio-svg__container {
    margin-right: 16px;
  }

  .skio-price {
    margin-left: auto;
  }

  .skio-price-subscription {
    color: rgb(252, 59, 85);
  }

  .skio-group-content {
    width: auto;

    transition: max-height 0.25s cubic-bezier(0.4,0,0.2,1),
                opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    max-height: 100px;
    opacity: 1;

    font-size: 13px;
  }

  /* Hide frequency if not selected */
  .skio-group-container:not(.skio-group-container--selected) .skio-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  }

  .skio-group-title {
    min-width: max-content;
    margin-right: 16px;
    text-transform: uppercase;
  }

  .skio-save {
    color: #0fa573;
    border: 1px #0fa573 solid;
    padding: 0px 8px;
    border-radius: 20px;
  }

  .skio-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 30px 8px 10px;
    margin-top: 5px;
    border-radius: 5px;
    background-color: #f7f7f7;
    width: 100%;
    border: 0;
    font-size: 14px;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    display: none
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
  }

  .skio-frequency span {
    text-transform: lowercase;
  }

  .skio-frequency-btns {
    display: flex;
  }

  .skio-frequency-copy {
    display: block;
    font-weight: 600;
    margin-top: 18px;
    margin-bottom: 15px;
  }

  .skio-frequency-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgb(229, 231, 235);
    border-right: 0;
    min-height: 50px;
    text-transform: capitalize;
    position: relative;
  }

  .skio-frequency-btn:last-child {
    margin-right: 0px;
    border-right: 1px solid rgb(229, 231, 235);
  }

  .skio-frequency-btn.selected:after {
    content: '';
    position: absolute;
    inset: 0;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border: 2px solid #000;
    z-index: 2;
  }

  .skio-subheading {
    font-family: SourceSansPro, futura-pt-condensed, sans-serif;
    text-transform: uppercase;
    font-weight: 900;
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },
    key: { type: String },
    formId: { type: String },

    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    subscriptionFirst: { type: Boolean },
    startSubscription: { type: Boolean },

    discount_format: { type: String },

    moneyFormatter: {},

    currency: { type: String },

    buttonSelector: { type: String }
  };

  static styles = skioStyles;

  constructor() {
    super();
    // Define reactive properties--updating a reactive property causes the component to update.
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.key = null;
    this.formId = null;

    this.skioSellingPlanGroups = null;
    this.availableSellingPlanGroups = null;

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';

    this.currency = 'USD';
    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    });

    this.buttonSelector = 'false';

  }

  connectedCallback() {
    const vm = this;
    super.connectedCallback();

    if(!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    window.addEventListener("coco-shuffle:product-updated", function() {
      vm.product = document.querySelector("coco-shuffle").selectedProduct;
      vm.selectedVariant = document.querySelector("coco-shuffle").currentVariant;
    });

    window.addEventListener("coco-shuffle:variant-updated", function() {
      vm.product = document.querySelector("coco-shuffle").selectedProduct;
      vm.selectedVariant = document.querySelector("coco-shuffle").currentVariant;
    });

  }

  disconnectedCallback() {

    window.removeEventListener("coco-shuffle:product-updated", function() {
      this.product = document.querySelector("coco-shuffle").selectedProduct;
      vm.selectedVariant = document.querySelector("coco-shuffle").currentVariant;
    });

    window.removeEventListener("coco-shuffle:variant-updated", function() {
      this.product = document.querySelector("coco-shuffle").selectedProduct;
      this.selectedVariant = document.querySelector("coco-shuffle").currentVariant;
    });

    super.disconnectedCallback();
  }

  render() {
    if(!this.product || !this.selectedVariant || this.availableSellingPlanGroups == '' ) return;

    return html`
      <span class="skio-subheading">Purchase Type</span>

      <fieldset class="skio-plan-picker" skio-plan-picker="${ this.key }">
        <input name="selling_plan" type="hidden" value="${ this.selectedSellingPlan ? this.selectedSellingPlan.id : ''}" />
        <input name="properties[Discount]" type="hidden" value="${ this.selectedSellingPlan ? this.discount(this.selectedSellingPlan).percent : '' }"
          ?disabled="${ this.selectedSellingPlan ? false : true }" />

        <div class="skio-group-container ${ this.product.requires_selling_plan == false ? 'skio-group-container--available' : '' } ${ this.selectedSellingPlanGroup == null ? 'skio-group-container--selected' : '' } ${ this.subscriptionFirst ? 'skio-onetime-second' : ''}" skio-group-container
          @click=${() => this.selectSellingPlanGroup(null) } >

          <input id="skio-one-time-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }" type="radio" value=""
            skio-one-time ?checked=${ this.startOnetime == true && this.product.requires_selling_plan == false ? true : false }>

          <label class="skio-group-label" for="skio-one-time-${ this.key }">
            <div class="skio-group-topline">
              <div class="skio-radio-svg__container">
                ${ this.selectedSellingPlanGroup == null ?
                  html`
                    <div class="skio-radio-svg skio-radio-svg--checked" aria-hidden="true" aria-label="Checked">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9">
                        <defs>
                            <clipPath id="ujbx43qefa">
                                <path data-name="Rectangle 49" d="M0 0h12v9H0z"/>
                            </clipPath>
                        </defs>
                        <path data-name="Path 24" d="M3.94 9a.705.705 0 0 1-.506-.215L0 5.264l1.013-1.039 2.927 3L10.987 0 12 1.039 4.446 8.785A.705.705 0 0 1 3.94 9" style="clip-path:url(#ujbx43qefa)"/>
                      </svg>
                    </div>
                  `
                  :
                  html`<div class="skio-radio-svg skio-radio-svg--unchecked" aria-hidden="true" aria-label="Unchecked"></div>`
                }
              </div>
              <div class="skio-group-title">
                One-time
              </div>
              <div class="skio-price">
                <span skio-onetime-price>${ this.moneyFormatter.format(this.selectedVariant.price / 100) }</span>
              </div>
            </div>
          </label>
        </div>

        ${ this.availableSellingPlanGroups ? this.availableSellingPlanGroups.map((group, index) =>
          html`
            <div class="skio-group-container skio-group-container--available ${ this.selectedSellingPlanGroup == group ? 'skio-group-container--selected' : '' }" skio-group-container
              @click=${() => this.selectSellingPlanGroup(group) }>
              <input id="skio-selling-plan-group-${ index }-${ this.key }" class="skio-group-input" name="skio-group-${ this.key }"
                type="radio" value="${ group.id }" skio-selling-plan-group="${ group.id }" ?checked=${ this.selectedSellingPlanGroup == group ? true : false } >
              <label class="skio-group-label" for="skio-selling-plan-group-${ index }-${ this.key }">
                <div class="skio-group-topline">
                <div class="skio-radio-svg__container">
                  ${ this.selectedSellingPlanGroup == group ?
                    html`
                      <div class="skio-radio-svg skio-radio-svg--checked" aria-hidden="true" aria-label="Checked">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9">
                          <defs>
                              <clipPath id="ujbx43qefa">
                                  <path data-name="Rectangle 49" d="M0 0h12v9H0z"/>
                              </clipPath>
                          </defs>
                          <path data-name="Path 24" d="M3.94 9a.705.705 0 0 1-.506-.215L0 5.264l1.013-1.039 2.927 3L10.987 0 12 1.039 4.446 8.785A.705.705 0 0 1 3.94 9" style="clip-path:url(#ujbx43qefa)"/>
                        </svg>
                      </div>
                    `
                    :
                    html`<div class="skio-radio-svg skio-radio-svg--unchecked" aria-hidden="true" aria-label="Unchecked"></div>`
                  }
                </div>
                  <div class="skio-group-title">
                    ${ group.name == 'Subscription' ? 'Subscribe & Save' : group.name }

                    ${ this.discount(group.selected_selling_plan).percent !== '0%' ? html`
                      <span skio-discount>${ this.discountFormat == 'percent' ? this.discount(group.selected_selling_plan).percent : this.discount(group.selected_selling_plan).amount }</span>
                    ` : html`` }
                  </div>
                  <div class="skio-price skio-price-subscription">
                    ${ this.discount(group.selected_selling_plan).percent !== '0%' ? html`
                      Save ${ this.money(this.discount(group.selected_selling_plan).amount) } (<span skio-discount>${ this.discount(group.selected_selling_plan).percent }</span>) -
                    ` : html`` }
                    <span skio-subscription-price>${ this.price(group.selected_selling_plan) }</span>
                  </div>
                </div>

                <div class="skio-group-content">

                  <select skio-selling-plans="${ group.id }" class="skio-frequency"
                    @change=${ (e) => this.selectSellingPlan(e.target.value, group) }>
                    ${ group ? group.selling_plans.map((selling_plan) =>
                        html`
                        <option value="${ selling_plan.id }">
                          ${ group.name == 'Subscription' ? `Delivery ${ selling_plan.name }` : `${ selling_plan.name }` }
                        </option>
                        `
                      ): ''}
                  </select>

                ${ this.buttonSelector !== 'false' ?
                  html`
                  <span class="skio-frequency-copy">Delivery Every</span>
                  <div class="skio-frequency-btns">
                    ${ group ? group.selling_plans.map((selling_plan) =>
                      html`
                        <div class="skio-frequency-btn${ selling_plan.id == this.selectedSellingPlan?.id ? ' selected' : '' }" data-value="${ selling_plan.id }" data-label="${ selling_plan.name }"
                        @click=${() => this.selectSellingPlan(selling_plan.id, group) }
                        >
                          ${ selling_plan.name.toLowerCase().replace("every ", "").replace("subscription -", "") }
                        </div>
                      `
                    ):''}
                  </div>
                  `
                : '' }

                </div>
              </label>
            </div>
          `
        ): ''}
      </fieldset>`
  }

  updated = (changed) => {

    if(changed.has('product') && this.product) {

      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group => selling_plan_group.app_id === 'SKIO'
      )

    }

    if(changed.has('selectedVariant') && this.selectedVariant) {

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(selling_plan_group =>
        this.selectedVariant.selling_plan_allocations.some(
          selling_plan_allocation => selling_plan_allocation.selling_plan_group_id === selling_plan_group.id
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups.length) {
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach((group => {
          group.selected_selling_plan = group.selling_plans[0];
        }));

        if (this.startOnetime == false || this.product.requires_selling_plan == true) {
          this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
          this.selectedSellingPlan = this.availableSellingPlanGroups[0].selling_plans[0];
        } else {

          if (this.selectedSellingPlan !== null) {
            //means a selling plan was already set; we want to keep the same frequency, so find it and set it
            let frequency = this.selectedSellingPlan.name;
            let selling_plan_group = this.availableSellingPlanGroups.find(x => x.selling_plans.find(y => y.name == frequency));
            let selling_plan = selling_plan_group.selling_plans.find(x => x.name == frequency);
            if (selling_plan) {
              this.selectedSellingPlanGroup = selling_plan_group;
              this.selectedSellingPlan = selling_plan;
            }
          } else {
            this.selectedSellingPlanGroup = null;
            this.selectedSellingPlan = null;
          }

        }

      }
    }

    if(changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${ this.key }"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan == null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key
        }
      });

      this.dispatchEvent(event);

      if (this.formId !== null) {
        let form = document.querySelector('#' + this.formId);
        if (form) {

          let selling_plan_value = this.selectedSellingPlan == null ? '' : this.selectedSellingPlan.id;
          let discount_value = this.selectedSellingPlan == null ? '' : this.discount(this.selectedSellingPlan).percent;

          if (!form.querySelector('[name="selling_plan"]')) {
            let selling_plan_input = document.createElement('input');
            selling_plan_input.type = "hidden";
            selling_plan_input.name = "selling_plan";
            selling_plan_input.value = selling_plan_value;
            form.append(selling_plan_input);
          } else {
            form.querySelector('[name="selling_plan"]').value = selling_plan_value;
          }

          if (!form.querySelector('[name="properties[Discount]"]')) {
            let discount_input = document.createElement('input');
            discount_input.type = "hidden";
            discount_input.name = "properties[Discount]";
            discount_input.value = discount_value;
            form.append(discount_input);
          } else {
            form.querySelector('[name="properties[Discount]"]').value = discount_value;
          }

        }
      }

    }
  }

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
  }

  error = (...args) =>  {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    window.dispatchEvent(new CustomEvent(`skio-plan-changed`, { detail: group }));
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
  }

  selectFrequency(selling_plan_id) {

  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id, group) {
    let selling_plan = group.selling_plans.find(x => x.id == selling_plan_id);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else this.log("Error: couldn't find selling plan with id " + selling_plan_id + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan)
      return { percent: '0%', amount: 0 }

    const price_adjustment = selling_plan.price_adjustments[0]
    const discount = { percent: '0%', amount: 0 }
    const price = this.selectedVariant.price;

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`
        discount.amount = Math.round(
          (price * price_adjustment.value) / 100.0
        )
        break
      case 'fixed_amount':
        discount.percent = `${Math.round(
          ((price_adjustment.value * 1.0) / price) * 100.0
        )}%`
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = `${Math.round(
          (((price - price_adjustment.value) * 1.0) /
            price) *
            100.0
        )}%`
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    const compareAt = this.selectedVariant.compare_at_price > this.selectedVariant.price ? this.selectedVariant.compare_at_price : this.selectedVariant.price;
    return formatted
      ? this.money(compareAt - this.discount(selling_plan).amount)
      : compareAt - this.discount(selling_plan).amount
  }

  // Optional functions keep if necessary

  /**
   *
   *
   */

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan.id })
      }
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    })
    .then((response) => response.json())
    .then((response) => {
      this.log("SKIO added item to cart: ", response);
      //dispatch CustomEvent to tell document that an item was added to cart
      const event = new CustomEvent(`skio::added-to-cart`, {
        bubbles: true,
        composed: true,
        detail: {
          response,
          key: this.key
        }
      });

      this.dispatchEvent(event);
    })
    .catch((error) => {
      this.error(`SKIO ${ this.key } error adding item to cart: `, error);
    });
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${ handle }.js`)
    .then((response) => response.json())
    .then((product) => {
      this.product = product;
      this.selectedVariant = product.variants[0];

      return product;
    });
  }

  attachAjaxSubmit() {
    const vm = this;
    // overrides the "submit" of any form with [skio-add-to-cart-form] to ensure that the
    // selling_plan input and properties[Discount] input are included in form submission; could also
    // have passed the formId has a param to auto-add the inputs to the form, either works

    if (this.formId !== null) {

      let form = document.querySelector('#' + this.formId);
      if (form) {
        if (form.querySelector('[name="id"]')) {
          if (form.querySelector('[name="id"]').value == document.querySelector('skio-plan-picker').selectedVariant.id) {
            form.addEventListener("submit", function(e) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              let selling_plan = document.querySelector('skio-plan-picker').selectedSellingPlan;
              if (selling_plan) {

                if (!form.querySelector('[name="selling_plan"]')) {
                  let selling_plan_input = document.createElement('input');
                  selling_plan_input.type = "hidden";
                  selling_plan_input.name = "selling_plan";
                  selling_plan_input.value = selling_plan.id;
                  form.append(selling_plan_input);
                } else {
                  form.querySelector('[name="selling_plan"]').value = selling_plan.id
                }

                if (!form.querySelector('[name="properties[Discount]"]')) {
                  let discount_input = document.createElement('input');
                  discount_input.type = "hidden";
                  discount_input.name = "properties[Discount]";
                  discount_input.value = document.querySelector('skio-plan-picker').discount(selling_plan).percent;
                  form.append(discount_input);
                } else {
                  form.querySelector('[name="properties[Discount]"]').value = document.querySelector('skio-plan-picker').discount(selling_plan).percent;
                }

              }
              form.submit();
            });
          }
        }
      }

      // overrides the "click" event of any element with [skio-add-to-cart-btn] and runs an addToCart; can add
      // [skio-quantity] and  [skio-key=${key}] to any input / select element to change the quantity for the
      // addToCart call from 1 to any user input value
      document.querySelectorAll(`[skio-add-to-cart-btn][skio-key="${ this.key }"]`).forEach((btn) => {
        btn.addEventListener("click", function(e) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          let quantity = 1;
          let quantity_el = document.querySelector(`[skio-quantity][skio-key="${ this.key }"`);
          if (quantity_el) quantity = quantity_el.value;
          vm.addToCart(quantity);
        });
      });

    }

  }

}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);